import { get } from '@/api/base';
import { RemoteData, state } from '@/interfaces/RemoteData';
import { VerseTableRow, VerseTableRowCompact } from '@/interfaces/ExplorerTableRow';
import { ref, computed, shallowRef } from 'vue';

// Performance-optimierte Datenstruktur mit markRaw für bessere Reaktivität
const compactOverview = shallowRef<RemoteData<VerseTableRowCompact[]>>({ state: state.NotAsked });

// Cache für gemappte Daten - nur die aktuell sichtbaren Zeilen
const mappedCache = new Map<string, VerseTableRow>();

// Ergebnis-Caching für Filter/Sort-Operationen
const RESULT_CACHE_LIMIT = 200;
const resultCache = new Map<string, { indices: number[] }>();
const countCache = new Map<string, number>();

// Hilfsfunktion für stabile Serialisierung
function stableStringify(o: any) {
  return JSON.stringify(o, Object.keys(o).sort());
}

/**
 * Performance-optimierte Mapping-Funktion - mapped nur einzelne Zeilen bei Bedarf
 */
function mapCompactRowToReadable(compactRow: VerseTableRowCompact): VerseTableRow {
  const cacheKey = `${compactRow.s}:${compactRow.v}`;

  if (!mappedCache.has(cacheKey)) {
    mappedCache.set(cacheKey, {
      sura: compactRow.s,
      verse: compactRow.v,
      readings_count: compactRow.r,
      references_count: compactRow.rf,
      sources_count: compactRow.sr,
      variants_count: compactRow.vr,
      manuscripts_count: compactRow.m,
      intertexts_count: compactRow.i,
    });
  }

  return mappedCache.get(cacheKey) || {
    sura: 0,
    verse: 0,
    readings_count: 0,
    references_count: 0,
    sources_count: 0,
    variants_count: 0,
    manuscripts_count: 0,
    intertexts_count: 0,
  };
}

function keyedSort<T>(
  array: T[],
  keyExtractor: (item: T) => number | string,
  ascending = true,
): number[] {
  const keyed = array.map((item, index) => ({
    key: keyExtractor(item),
    index,
  }));

  keyed.sort((a, b) => {
    if (a.key < b.key) return ascending ? -1 : 1;
    if (a.key > b.key) return ascending ? 1 : -1;
    return 0;
  });

  return keyed.map((item) => item.index);
}

function getSortedIndices(
  compactData: VerseTableRowCompact[],
  filteredIndices: number[],
  sortKey: string,
  sortAsc: boolean,
): number[] {
  if (filteredIndices.length === 0) return [];

  if (sortKey === 'sura') {
    return keyedSort(
      filteredIndices.map((i) => compactData[i]),
      (row) => row.s * 1000 + row.v,
      sortAsc,
    ).map((sortedIdx) => filteredIndices[sortedIdx]);
  }

  const keyExtractor = (row: VerseTableRowCompact): number => {
    switch (sortKey) {
      case 'verse': return row.v;
      case 'readings_count': return row.r;
      case 'references_count': return row.rf;
      case 'sources_count': return row.sr;
      case 'variants_count': return row.vr;
      case 'manuscripts_count': return row.m;
      case 'intertexts_count': return row.i;
      default: return 0;
    }
  };

  return keyedSort(
    filteredIndices.map((i) => compactData[i]),
    keyExtractor,
    sortAsc,
  ).map((sortedIdx) => filteredIndices[sortedIdx]);
}

function fetchOverview(): void {
  get('/api/data/explorer', compactOverview);
}

function getFilteredIndices(
  compactData: VerseTableRowCompact[],
  filters: Record<string, [number, number]>,
): number[] {
  if (compactData.length === 0) return [];

  const activeFilters = Object
    .entries(filters)
    .filter(([, [min, max]]) => min !== undefined && max !== undefined);

  if (activeFilters.length === 0) {
    return Array.from({ length: compactData.length }, (_, i) => i);
  }

  return compactData.reduce<number[]>((indices, row, i) => {
    const matches = activeFilters.every(([key, [min, max]]) => {
      const value = (() => {
        switch (key) {
          case 'sura': return row.s;
          case 'verse': return row.v;
          case 'readings_count': return row.r;
          case 'references_count': return row.rf;
          case 'sources_count': return row.sr;
          case 'variants_count': return row.vr;
          case 'manuscripts_count': return row.m;
          case 'intertexts_count': return row.i;
          default: return 0;
        }
      })();
      return value >= min && value <= max;
    });

    if (matches) indices.push(i);
    return indices;
  }, []);
}

function getProcessedIndices(
  filters: Record<string, [number, number]>,
  sortKey: string,
  sortAsc: boolean,
): number[] {
  const cacheKey = `${stableStringify(filters)}|${sortKey}|${sortAsc ? 1 : 0}`;
  if (resultCache.has(cacheKey)) {
    return resultCache.get(cacheKey)!.indices;
  }
  let compactData: VerseTableRowCompact[] = [];
  if (compactOverview.value.state === state.Success && compactOverview.value.payload) {
    compactData = compactOverview.value.payload;
  }
  const filteredIndices = getFilteredIndices(compactData, filters);
  const sortedIndices = getSortedIndices(compactData, filteredIndices, sortKey, sortAsc);
  resultCache.set(cacheKey, { indices: sortedIndices });
  return sortedIndices;
}

function getTotalFilteredCount(filters: Record<string, [number, number]>): number {
  if (compactOverview.value.state !== state.Success || !compactOverview.value.payload) {
    return 0;
  }
  const countKey = stableStringify(filters);
  if (countCache.has(countKey)) return countCache.get(countKey)!;
  // Suche ein beliebiges Ergebnis mit diesen Filtern im Ergebnis-Cache
  let found = false;
  let foundLength = 0;
  Array.from(resultCache.entries()).some(([key, value]) => {
    if (key.startsWith(`${countKey}|`)) {
      found = true;
      foundLength = value.indices.length;
      return true;
    }
    return false;
  });
  if (found) {
    countCache.set(countKey, foundLength);
    return foundLength;
  }
  // Falls nicht vorhanden, berechne und cache
  const indices = getProcessedIndices(filters, 'sura', true);
  countCache.set(countKey, indices.length);
  return indices.length;
}

export {
  compactOverview,
  fetchOverview,
  getProcessedIndices,
  getTotalFilteredCount,
};
